import { createContext } from 'preact';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';
import { AlmostEqual } from '../../math';
import { changes } from '../../subs';
import { teadsApi } from '../../teads-api';
import { Change } from '../../types';
import { PreviewContext } from '../contexts/previewContext';
import { useAdWindow } from './useAdWindow';

type Size = { width: number; height: number };
export const MaxSlotSizeDefaultValue = createContext({ width: 0, height: 0 });

export function useMaxSlotSize() {
  const win = useAdWindow();
  const defaultSize = useContext(MaxSlotSizeDefaultValue);
  const [size, _setSize] = useState(defaultSize);
  const preview = useContext(PreviewContext);
  useEffect(() => {
    if (preview) {
      _setSize(defaultSize);
    }
  }, [defaultSize, preview]);

  const setSize = useCallback((value: Size | ((s: Size) => Size)) => {
    _setSize((state) => {
      value = typeof value === 'function' ? value(state) : value;
      return AlmostEqual(state.width, value.width) && AlmostEqual(state.height, value.height) ? state : value;
    });
  }, []);

  const updateSize = useCallback(() => {
    teadsApi.getSlotBounds((err, value) => {
      if (!err) {
        const width = value.maxWidth || value.width || value.viewportWidth || 0;
        const height = value.maxHeight || value.viewportHeight || 0;

        setSize((size) => ({
          width: width < 2 ? size.width : width,
          height: height < 2 ? size.height : height,
        }));
      }
    });
  }, []);
  const listener = useCallback(() => updateSize(), [updateSize]);

  useEffect(listener, []);

  useEffect(() => {
    const listener = ({ event }: Change) => {
      if (event == 'AdSizeChange') {
        updateSize();
      }
    };

    changes.subscribe(listener);
    return () => changes.unsubscribe(listener);
  }, [updateSize]);

  useEffect(() => {
    win.addEventListener('resize', listener);
    win.addEventListener('orientationchange', listener);
    win.screen?.orientation?.addEventListener?.('change', listener);
    return () => {
      win.removeEventListener('resize', listener);
      win.removeEventListener('orientationchange', listener);
      win.screen?.orientation?.removeEventListener?.('change', listener);
    };
  }, [win, listener]);

  return size;
}
